import { ActionContext } from "vuex";
import contractService from "@/services/contractService";
import {
  ContractListParams,
  ContractListItemDto,
  ContractResponseDto,
  CreateContractDto,
  UpdateContractDto,
  ApproveContractDto,
  CancelContractDto,
  ContractDynamicFieldDto,
  CreateDynamicFieldDto,
} from "@/models/contract.model";
import {
  CreateDeferredDownPaymentDto,
  DeferredDownPaymentOverdueReportDto,
  DeferredDownPaymentResponseDto,
  PayDeferredDownPaymentDto,
  UpdateDeferredDownPaymentDto,
} from "@/models/deferredDownPayment.model";

interface State {
  contracts: ContractListItemDto[];
  total: number;
  currentContract: ContractResponseDto | null;
  dynamicFields: ContractDynamicFieldDto[];
}

type Ctx = ActionContext<State, any>;

const state: State = {
  contracts: [],
  total: 0,
  currentContract: null,
  dynamicFields: [],
};

const mutations = {
  setContracts(state: State, payload: { items: ContractListItemDto[]; total: number }) {
    state.contracts = payload.items;
    state.total = payload.total;
  },
  setCurrentContract(state: State, payload: ContractResponseDto | null) {
    state.currentContract = payload;
  },
  setDynamicFields(state: State, payload: ContractDynamicFieldDto[]) {
    state.dynamicFields = payload;
  },
};

const actions = {
  async loadContracts({ commit, dispatch }: Ctx, params: ContractListParams = {}) {
    try {
      const response = await contractService.listContracts(params);
      commit("setContracts", response);
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchContract({ commit, dispatch }: Ctx, contractId: string) {
    try {
      const contract = await contractService.getContractById(contractId);
      commit("setCurrentContract", contract);
      return contract;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      commit("setCurrentContract", null);
      throw error;
    }
  },

  async createContract({ dispatch }: Ctx, payload: CreateContractDto): Promise<ContractResponseDto> {
    try {
      const response = await contractService.createContract(payload);
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async updateContract({ dispatch }: Ctx, payload: { contractId: string; data: UpdateContractDto }): Promise<ContractResponseDto> {
    try {
      const response = await contractService.updateContract(payload.contractId, payload.data);
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async approveContract({ dispatch }: Ctx, payload: { contractId: string; data: ApproveContractDto }): Promise<ContractResponseDto> {
    try {
      const response = await contractService.approveContract(payload.contractId, payload.data);
      dispatch("notificator/success", "contract-approve-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async cancelContract({ dispatch }: Ctx, payload: { contractId: string; data: CancelContractDto }): Promise<ContractResponseDto> {
    try {
      const response = await contractService.cancelContract(payload.contractId, payload.data);
      dispatch("notificator/success", "contract-cancel-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async loadDynamicFields({ commit, dispatch }: Ctx) {
    try {
      const fields = await contractService.getDynamicFields();
      commit("setDynamicFields", fields);
      return fields;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async createDynamicField({ dispatch }: Ctx, payload: CreateDynamicFieldDto): Promise<ContractDynamicFieldDto> {
    try {
      const field = await contractService.createDynamicField(payload);
      await dispatch("loadDynamicFields");
      return field;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async deleteDynamicField({ dispatch }: Ctx, fieldId: string): Promise<void> {
    try {
      await contractService.deleteDynamicField(fieldId);
      await dispatch("loadDynamicFields");
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchDeferredDownPayments({ dispatch }: Ctx, contractId: string): Promise<DeferredDownPaymentResponseDto[]> {
    try {
      return await contractService.listDeferredDownPayments(contractId);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async createDeferredDownPayment({ dispatch }: Ctx, payload: CreateDeferredDownPaymentDto): Promise<DeferredDownPaymentResponseDto> {
    try {
      return await contractService.createDeferredDownPayment(payload);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async updateDeferredDownPayment(
    { dispatch }: Ctx,
    payload: { deferredDownPaymentId: string; data: UpdateDeferredDownPaymentDto },
  ): Promise<DeferredDownPaymentResponseDto> {
    try {
      return await contractService.updateDeferredDownPayment(payload.deferredDownPaymentId, payload.data);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async deleteDeferredDownPayment({ dispatch }: Ctx, deferredDownPaymentId: string): Promise<void> {
    try {
      await contractService.deleteDeferredDownPayment(deferredDownPaymentId);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async payDeferredDownPayment(
    { dispatch }: Ctx,
    payload: { deferredDownPaymentId: string; data: PayDeferredDownPaymentDto },
  ): Promise<DeferredDownPaymentResponseDto> {
    try {
      return await contractService.payDeferredDownPayment(payload.deferredDownPaymentId, payload.data);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchDeferredDownPaymentOverdueReport(
    { dispatch }: Ctx,
    params: { asOf?: string } = {},
  ): Promise<DeferredDownPaymentOverdueReportDto> {
    try {
      return await contractService.getDeferredDownPaymentOverdueReport(params);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },
};

const getters = {
  contracts: (state: State) => state.contracts,
  total: (state: State) => state.total,
  currentContract: (state: State) => state.currentContract,
  dynamicFields: (state: State) => state.dynamicFields,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
